import {
  action,
  createMachine,
  reduce,
  state,
  transition,
  Transition
} from "robot3";
import {StateMachineContext} from "./StateMachineContext";
import {
  ReviewEvent,
  CreateEvent,
  SuccessEvent,
  FailureEvent,
  RetryEvent,
  StepBackEvent
} from "./events";

const context = (
  input: Omit<StateMachineContext, "stepper" | "step" | "controls">
): StateMachineContext => ({
  ...input,
  stepper: () => null,
  step: () => null,
  controls: () => null
});

/**
 * 5-state machine for the Create Entity flow:
 *
 * form → review-plan → in-progress → success
 *                                  → failure → form (step-back)
 *                                            → in-progress (retry)
 *
 * The extra `form` state (vs create-organization's 4 states) handles the
 * editable form step. The `review` event transitions from form to review-plan
 * when the user submits the form.
 */
export const stateMachine = createMachine(
  {
    "form": state(
      transition(
        "review",
        "review-plan",
        reduce<StateMachineContext, ReviewEvent>((ctx) => ({
          ...ctx
        }))
      )
    ),
    "review-plan": state<Transition<string>>(
      transition(
        "create",
        "in-progress",
        action<StateMachineContext, CreateEvent>((ctx, {plan}) => {
          ctx.clearFeedback();
          ctx.createEntity(plan).catch(err => {
            console.error("Failed to fire mutation:", err);
          });
        }),
        reduce<StateMachineContext, CreateEvent>((ctx) => ({
          ...ctx
        }))
      ),
      transition(
        "step-back",
        "form",
        reduce<StateMachineContext, StepBackEvent>((ctx) => ({
          ...ctx
        }))
      )
    ),
    "in-progress": state<Transition<string>>(
      transition(
        "success",
        "success",
        reduce<StateMachineContext, SuccessEvent>((ctx, {result}) => {
          ctx.showFeedback({
            type: "success",
            message: ctx.translate("create-new-entity.feedback.success"),
            autoHide: true,
            onClose: ctx.clearFeedback
          });
          return {
            ...ctx
          };
        })
      ),
      transition(
        "failure",
        "failure",
        reduce<StateMachineContext, FailureEvent>((ctx, {result}) => {
          ctx.showFeedback({
            type: "error",
            message: result.message || ctx.translate("create-new-entity.errors.generic"),
            autoHide: true,
            onClose: ctx.clearFeedback
          });
          return {
            ...ctx
          };
        })
      )
    ),
    "success": state(),
    "failure": state<Transition<string>>(
      transition(
        "step-back",
        "form",
        reduce<StateMachineContext, StepBackEvent>((ctx) => ({
          ...ctx
        }))
      ),
      transition(
        "retry",
        "in-progress",
        action<StateMachineContext, RetryEvent>((ctx, {plan}) => {
          ctx.clearFeedback();
          ctx.createEntity(plan).catch(err => {
            console.error("Failed to fire retry mutation:", err);
          });
        }),
        reduce<StateMachineContext, RetryEvent>((ctx) => ({
          ...ctx
        }))
      )
    )
  },
  (input: Omit<StateMachineContext, "stepper" | "step" | "controls">) => {
    return context(input);
  }
);
